// What a labelled blank on a form is ASKING FOR.
//
// CONSUMER: shared (web prep screen + node-pool tests).
//
// Nate, 2026-09-01, on the AAR Residential Listing Contract: "the mapping
// is incorrect for this listing agreement… no listing info has fields
// filled in." Both halves of that are one gap. The suggestion ladder in
// field-suggestions.ts finds WHERE the blanks are; nothing has ever
// decided WHAT they are for. So even a perfectly-placed field arrived
// empty, and every blank on an eight-page contract was an advisor's job
// to type.
//
// This is the missing half: a blank labelled "Assessor's #" wants the
// parcel number, and the OS already knows it. The rules below turn the
// label the form prints next to a line into the merge token that fills
// it, so the contract arrives populated from the record instead of
// waiting for someone to retype what the OS is already authoritative for.
//
// Deterministic on purpose. This runs BEFORE the model and costs nothing,
// which matters because the AAR catalog is 27 forms that ask the same
// dozen questions in the same words on every one of them — that is a
// dictionary, not a reasoning problem.
//
// Pure and dependency-free, the field-types.ts rule.

import type { EsignFieldType } from "./field-types";

/** A binding a labelled blank resolves to. */
export interface LabelBinding {
  /** The merge token this blank should be bound to. */
  token: string;
  /** Plain English for the field chip: "Property address", not a token. */
  label: string;
}

interface BindingRule extends LabelBinding {
  pattern: RegExp;
  /** Company slugs this rule applies to. Omitted means every company.
   *
   *  Scoped because the same word means different things per company:
   *  "county" on a listing contract is the property's county, while on
   *  an insurance form it could be anything. A rule that cannot be
   *  stated confidently for a company simply does not apply there. */
  companies?: readonly string[];
}

const REALTY = ["realty"] as const;
const PRODUCTIONS = ["productions"] as const;

/**
 * A label longer than this is prose, not a caption.
 *
 * The single biggest false-positive risk here is a legal paragraph that
 * happens to contain "county" or "state" binding a field in the middle
 * of a sentence. Real captions on a form are short — "Assessor's #:",
 * "Property Address", "Printed Name". Requiring brevity is what makes a
 * dictionary safe to run over a document that is 80% dense legalese.
 */
const MAX_LABEL_CHARS = 60;

/**
 * The dictionary, most specific first — the first match wins, so
 * "mailing address" resolves as the owner's mailing address rather than
 * matching the looser property-address rule underneath it.
 *
 * DELIBERATELY ABSENT: compensation percentages (the Listing Contract's
 * §4, the Buyer-Broker agreement's §2). The OS has the tokens for them
 * (service_record.commission_rate, service_record.unrepresented_buyer_rate)
 * and binding them would demo well — but Grant's 2026-08-30 call on that
 * exact number was that it "could be different each time and will change
 * in different markets. It needs to be flexible," after Nate and Mark
 * gave two different answers about how it is even calculated. A rate
 * auto-printed onto a legal compensation clause is the accuracy failure
 * CLAUDE.md forbids: wrong, confident, and acted on. So §4 stays a field
 * the agent fills or binds deliberately, per listing.
 */
const RULES: readonly BindingRule[] = [
  // ── The property ──
  {
    pattern: /\b(premises|property)\s+address\b/i,
    token: "service_record.property_address",
    label: "Property address",
    companies: REALTY,
  },
  {
    pattern: /\baddress\s+of\s+(the\s+)?(premises|property)\b/i,
    token: "service_record.property_address",
    label: "Property address",
    companies: REALTY,
  },
  {
    pattern: /\b(assessor'?s?|parcel|tax)\s*(id|#|no\.?|number)(?!\w)/i,
    token: "service_record.parcel_number",
    label: "Assessor's parcel number",
    companies: REALTY,
  },
  { pattern: /\bapn\b/i, token: "service_record.parcel_number", label: "Assessor's parcel number", companies: REALTY },
  {
    pattern: /\blegal\s+description\b/i,
    token: "service_record.legal_description",
    label: "Legal description",
    companies: REALTY,
  },
  { pattern: /\bcounty\b/i, token: "service_record.county", label: "County", companies: REALTY },
  {
    pattern: /\byear\s+built\b/i,
    token: "service_record.year_built",
    label: "Year built",
    companies: REALTY,
  },

  // ── The agreement's own dates (Listing §3, Buyer-Broker §1) ──
  {
    pattern: /\bcommencement\s+date\b/i,
    token: "service_record.agreement_signed_date",
    label: "Commencement date",
    companies: REALTY,
  },
  {
    pattern: /\bexpiration\s+date\b/i,
    token: "service_record.agreement_expiration_date",
    label: "Expiration date",
    companies: REALTY,
  },

  // ── The transaction ──
  {
    pattern: /\bearnest\s+money\b/i,
    token: "transaction.earnest_money",
    label: "Earnest money",
    companies: REALTY,
  },
  {
    pattern: /\b(escrow|title)\s+(company|agent)\b/i,
    token: "transaction.escrow_company_name",
    label: "Escrow / title company",
    companies: REALTY,
  },

  // ── The licensee signing block ──
  {
    pattern: /\blicense\s*(#|no\.?|number)(?!\w)/i,
    token: "advisor.license_number",
    label: "Your real-estate license #",
    companies: REALTY,
  },

  // ── The person ──
  //
  // Bound to the SIGNER's contact tokens, which is why these are safe on
  // every company: whoever the field is assigned to is who it prints.
  // Nate, 2026-09-09: these captions ask for the name on the signer's
  // ID — "Buyer:", "Seller's Name", "Printed Name" — which is exactly
  // what contact.legal_full_name is (legal first + legal last, no
  // nickname substitution; see merge-fields.ts). contact.full_name is
  // the friendlier greeting name and stays the right default for an
  // email or letter, but was wrong here: it silently prints a nickname
  // onto a legal signature block when a contact has one on file.
  {
    pattern: /\b(printed|print)\s+name\b/i,
    token: "contact.legal_full_name",
    label: "Legal full name",
  },
  {
    pattern: /\b(owner|seller|buyer|borrower|applicant|insured)'?s?\s+name\b/i,
    token: "contact.legal_full_name",
    label: "Legal full name",
  },
  // Stewardship Productions paperwork (2026-10-03) goes to team members,
  // who are not contacts, so the two rules above can never bind there
  // (contact tokens are not offered on a Productions form). The name on
  // the send is the only name such a form can print, and the same
  // captions are asking for it.
  {
    pattern: /\b(printed|print|full|legal|team\s+member'?s?|employee'?s?|contractor'?s?|agent'?s?)\s+(full\s+)?name\b/i,
    token: "signer.name",
    label: "Signer's name (as it is on the send)",
    companies: PRODUCTIONS,
  },
  { pattern: /\be-?mail(\s+address)?\b/i, token: "contact.email", label: "Email address" },
  {
    pattern: /\b(phone|telephone|mobile|cell)\s*(#|no\.?|number)?(?!\w)/i,
    token: "contact.phone",
    label: "Phone number",
  },

  // ── The person's address block ──
  //
  // AFTER the property rules above: on a listing contract both appear,
  // and "Premises Address" must not resolve to where the owner gets mail.
  {
    pattern: /\bmailing\s+address\b/i,
    token: "household.mailing_address",
    label: "Mailing address",
  },
  { pattern: /\bcity\b/i, token: "household.city", label: "City" },
  { pattern: /\bstate\b/i, token: "household.state", label: "State" },
];

/**
 * Captions that name MORE THAN ONE fact, and therefore bind to none.
 *
 * WHY (2026-09-08, the AAR signature block): the listing contract
 * captions a single blank "CITY, STATE, ZIP CODE". The city rule below
 * matches it, so it would bind to `household.city` and print "Gilbert"
 * into a blank that is asking for "Gilbert, AZ 85234". The OS has no
 * token for the compound, and the honest answer to "I can fill part of
 * this" on a legal document is to fill none of it — an empty box costs
 * a few seconds, a half-answered address on a listing contract is a
 * wrong fact that looks deliberate (CLAUDE.md's accuracy rule).
 *
 * The same reasoning covers a bare "ADDRESS" in an owner block: the
 * only address token we hold is the FULL mailing address, city and zip
 * included, and printing that into a street-only line duplicates what
 * the next blank asks for.
 *
 * Checked before every rule, so adding a compound caption here is how
 * you stop a binding without weakening the rule that was catching it.
 */
const NEVER_BIND: readonly RegExp[] = [
  // "CITY, STATE, ZIP CODE" / "City/State/Zip" / "City, State Zip"
  /\bcity\b[\s,/]+\bstate\b/i,
  /\bstate\b[\s,/]+\bzip\b/i,
  // A bare street-address caption, with no token that means only that.
  /^address$/i,
  /^(owner'?s?|seller'?s?|buyer'?s?)\s+address$/i,
];

/**
 * A caption this company has been taught, as stored by
 * shared/db/esign-caption-bindings.ts.
 *
 * `token` may be null: the lesson can be purely "this caption is a
 * signature line", with nothing to merge into it.
 */
export interface LearnedCaption {
  token: string | null;
  label: string;
  fieldType: EsignFieldType | null;
}

export interface BindingLookup {
  /** Which company's document this is, when known. */
  companySlug?: string | null;
  /**
   * What THIS COMPANY has already been taught a caption means.
   *
   * Nate, 2026-09-08: "Is there some sort of training that I could
   * provide so that this process is made easier for the OS?" This is
   * that training. Every caption a person binds by hand while mapping a
   * form is remembered for the company (see
   * shared/db/esign-caption-bindings.ts), and AAR reuses the same
   * captions across all 28 of its forms — so teaching the listing
   * contract what "PRINTED NAME OF FIRM" means teaches the other 27.
   *
   * Keyed by the caption normalized through `captionKey`. Consulted
   * BEFORE the built-in dictionary, because a person who corrected this
   * caption on a real contract knows better than a regular expression:
   * a learned answer that never wins is not learning.
   */
  learned?: ReadonlyMap<string, LearnedCaption> | null;
  /**
   * Whether a token can actually be filled on THIS record.
   *
   * Required, and deliberately not optional-with-a-permissive-default:
   * the catalog is grain-specific (a transaction offers no policy
   * fields), and binding a field to a token this record cannot resolve
   * produces a blank on a contract — which the send path then refuses,
   * turning a helpful guess into a blocked send. Fails closed.
   */
  tokenIsOffered: (token: string) => boolean;
}

/**
 * The binding a form's label is asking for, or null.
 *
 * `text` is the label ONLY — the caller strips the blank first (see
 * suggestFromText), because "City ____________" and "City" must resolve
 * the same way and an underscore run would otherwise blow the length
 * guard.
 *
 * Returns null far more often than it returns a rule, on purpose. An
 * unbound field is one the advisor fills in a few seconds; a wrongly
 * bound one prints the wrong fact onto a legal document and looks
 * deliberate doing it.
 */
export function bindingForLabel(
  text: string,
  lookup: BindingLookup,
): LabelBinding | null {
  const label = text.replace(/[\s:_.·—–-]+$/g, "").trim();
  if (!label || label.length > MAX_LABEL_CHARS) return null;
  if (NEVER_BIND.some((p) => p.test(label))) return null;

  // What a person taught this company beats what the dictionary infers.
  // Still gated on `tokenIsOffered`: a token that was renamed, or that
  // this record's grain cannot fill, must not come back from memory and
  // block a send — the same fails-closed rule the built-in rules follow.
  const remembered = lookup.learned?.get(captionKey(label));
  if (remembered?.token && lookup.tokenIsOffered(remembered.token)) {
    return { token: remembered.token, label: remembered.label };
  }

  const company = lookup.companySlug ?? null;
  for (const rule of RULES) {
    if (rule.companies && (!company || !rule.companies.includes(company))) continue;
    if (!rule.pattern.test(label)) continue;
    if (!lookup.tokenIsOffered(rule.token)) continue;
    return { token: rule.token, label: rule.label };
  }
  return null;
}

/**
 * The key a caption is remembered under.
 *
 * Two exports of the same form caption the same blank differently in
 * ways that carry no meaning — case, the trailing colon a form prints,
 * smart quotes, the section number in front of it. All of that is
 * absorbed so "OWNER\u2019S PRINTED NAME" and "Owner's printed name:"
 * are one lesson rather than two.
 *
 * What is NOT absorbed: different words. "Owner's Signature" and
 * "Seller's Signature" stay distinct keys, for the reason
 * normalizeAnchorText gives at length — one party's answer on the
 * other party's line is the worst thing this feature could do.
 */
export function captionKey(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/["'\u2018\u2019\u201c\u201d]/g, "'")
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u00a0/g, " ")
    .replace(/_{2,}/g, " ")
    // Section and paragraph numbers renumber between revisions, and a
    // stored anchor has ALREADY collapsed them (normalizeAnchorText does
    // the same thing for the same reason). Both sides must agree or a
    // lesson taught from an anchor is never found by a raw caption.
    .replace(/\d+/g, "#")
    .replace(/#(?:\s*[,./-]\s*#)+/g, "#")
    .replace(/\s+/g, " ")
    .replace(/^[\s:.,;\u00b7#*()-]+/, "")
    .replace(/[\s:.,;\u00b7*()-]+$/, "")
    .trim()
    .slice(0, 120);
}

/**
 * The field type a company has been TAUGHT this caption is, or null.
 *
 * Separate from `bindingForLabel` because the two answers are used at
 * different moments: the type decides what kind of box to draw, the
 * binding decides what goes in it, and a caption can teach either
 * without the other.
 *
 * Only ceremony types are ever stored (see LEARNABLE_TYPES in
 * shared/db/esign-caption-bindings.ts), so this can never quietly turn
 * somebody's signature line into a text box — the worst it can do is
 * mark a line as a place to sign, which is visible on the page and one
 * click to change.
 */
export function learnedCeremonyForLabel(
  text: string,
  lookup: BindingLookup,
): EsignFieldType | null {
  if (!lookup.learned) return null;
  const label = text.replace(/[\s:_.·—–-]+$/g, "").trim();
  if (!label || label.length > MAX_LABEL_CHARS) return null;
  return lookup.learned.get(captionKey(label))?.fieldType ?? null;
}

/** Every token the dictionary can produce. Exported so a test can assert
 *  each one still exists in the merge catalog — a rule pointing at a
 *  token that was renamed would silently stop binding, and silence is
 *  exactly how this feature fails without anyone noticing. */
export function dictionaryTokens(): string[] {
  return [...new Set(RULES.map((r) => r.token))];
}
